import { Pack, ProgramOrGuideItem, RenderablePackItem } from '@/components/app/cards/PacksList/types';

const SEPARATOR = ' • ';

// Mobile title-cases every word ("sleep_story" -> "Sleep Story").
const humanizeRowType = (type?: string): string | undefined =>
	type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : undefined;

const compacted = (parts: Array<string | undefined>): string[] =>
	parts.filter((part, index, all): part is string => Boolean(part) && all.indexOf(part) === index);

const joinParts = (parts: string[]): string | undefined => (parts.length ? parts.join(SEPARATOR) : undefined);

// Trims the " - " (sometimes NBSP before the hyphen) left once a daily subtitle's title half is dropped.
const stripTrailingJoiner = (text: string): string => text.replace(/[\s\u00A0\-•]+$/u, '');

export const getRowLines = ({
	itemType,
	contentType,
	narratorName,
	subtitle,
	duration,
	description,
	promotionalText,
}: {
	itemType?: string;
	contentType?: string;
	narratorName?: string;
	subtitle?: string;
	duration?: string;
	description?: string;
	promotionalText?: string;
}) => {
	// An activity's content type trails line 3 instead of leading line 2.
	const isActivity = itemType === 'activity';
	const type = humanizeRowType(contentType);
	const tertiaryParts = compacted([duration, description, promotionalText, isActivity ? type : undefined]);
	const secondaryParts = compacted([isActivity ? undefined : type, narratorName, subtitle]);

	const tertiary = tertiaryParts.filter(part => !secondaryParts.includes(part));
	// A daily subtitle reads "{date} - {session title}" and duplicates line 3's promotional text as
	// its suffix. The subtitle is dropped from line 2, but its leading date moves to the front of line 3.
	const leadingFragments: string[] = [];
	const secondary = secondaryParts.filter(part => {
		const matched = tertiary.find(t => part.includes(t));
		if (matched === undefined) {
			return true;
		}
		if (part === subtitle && part !== matched && part.endsWith(matched)) {
			const remainder = stripTrailingJoiner(part.slice(0, -matched.length));
			if (remainder) {
				leadingFragments.push(remainder);
			}
		}
		return false;
	});

	return {
		secondary: joinParts(secondary),
		tertiary: joinParts([...leadingFragments, ...tertiary]),
	};
};

export const isPlayableTrack = (item: RenderablePackItem): item is ProgramOrGuideItem => {
	const guide = item as ProgramOrGuideItem;
	return Boolean(guide.guide_id) && guide.asset_type !== 'video';
};

export const isEpisodicPack = (pack: Pack): boolean => pack.pack_type === 'episodic';

// Floored minutes with hours always shown. Returns undefined unless every item has a duration,
// since a partial sum would understate the playlist's length.
export const getPlaylistDuration = (
	items: Pack['items'],
	totalItemNumber?: number,
): { hours: number; minutes: number } | undefined => {
	// A partial page of a longer playlist would understate its length.
	if (totalItemNumber !== undefined && items.length < totalItemNumber) {
		return undefined;
	}
	const durations = items.map(item =>
		'duration' in item && typeof item.duration === 'number' ? item.duration : undefined,
	);
	if (durations.length === 0 || durations.some(d => d === undefined || d <= 0)) {
		return undefined;
	}
	const totalMinutes = Math.floor((durations as number[]).reduce((a, b) => a + b, 0) / 60);
	return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
};
